import { Check, Copy, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { cn } from '@/lib/utils';
import { AdPreview } from './AdPreview';
import { MetaShell, Toggle } from './MetaShell';
import { isLive, kr, int, type Period, periodLabel, pctStr, resultKind, short, useMetaStructure } from './model';
import {
  ANGLE_BY_ID,
  ANGLES,
  type AngleId,
  BUDGET_PER_DAY,
  buildMatrix,
  type CellStats,
  type CellStatus,
  cellKey,
  cellStatus,
  type Dest,
  dnaName,
  FORMATS,
  type Format,
  GOAL_PER_DAY,
  ROUND1_SENDER,
  ROUNDS,
  RHYTHM,
  RULES,
  SENDER_BY_ID,
  SENDERS,
  type SenderId,
} from './production';

/*
 * Meta → Produksjon. The ad production system as one picture: every angle
 * (why she clicks) against every sender (who says it). Each square is a
 * combination — has it been made, tested, did it win? Click one to see its ads
 * and what to make next. Overview only: nothing here changes Meta.
 */

const STATUS: Record<CellStatus, { label: string; cls: string }> = {
  untested: { label: 'Ikke laget', cls: 'border-dashed border-border bg-transparent' },
  young: { label: 'For lite data', cls: 'border-border bg-surface-2' },
  winner: { label: 'Vinner', cls: 'border-good/60 bg-good-tint' },
  ok: { label: 'Middels', cls: 'border-border-strong bg-surface-3' },
  loser: { label: 'Taper', cls: 'border-bad/50 bg-bad-tint' },
};

type Pick = { angle: AngleId | null; sender: SenderId | null } | null;

export function MetaProductionPage() {
  const q = useMetaStructure();
  const [period, setPeriod] = useState<Period>('lifetime');
  const [pick, setPick] = useState<Pick>(null);
  const [preview, setPreview] = useState<{ id: string; name: string } | null>(null);
  const d = q.data?.data;
  const matrix = useMemo(() => (d ? buildMatrix(d, period) : null), [d, period]);

  if (q.isLoading) return <LoadingState />;
  if (q.isError || !d || !matrix) return <ErrorState onRetry={() => void q.refetch()} />;

  const fx = d.fxAedToNok;
  const perDay = d.totals.recent.schedules / Math.max(1, d.recentDays);
  const liveBudget =
    d.campaigns
      .filter(isLive)
      .reduce((n, c) => n + (c.dailyBudget ?? c.adsets.filter(isLive).reduce((m, s) => m + (s.dailyBudget ?? 0), 0)), 0) * fx;
  const hasAngleless = [...matrix.cells.keys()].some((k) => k.startsWith('?|'));
  const hasSenderless = [...matrix.cells.keys()].some((k) => k.endsWith('|?'));
  const rows: (AngleId | null)[] = [...ANGLES.map((a) => a.id), ...(hasAngleless ? [null] : [])];
  const cols: (SenderId | null)[] = [...SENDERS.map((s) => s.id), ...(hasSenderless ? [null] : [])];
  const tested = ANGLES.flatMap((a) => SENDERS.map((s) => cellStatus(matrix.cells.get(cellKey(a.id, s.id))))).filter(
    (s) => s !== 'untested',
  ).length;
  const round1Done = ANGLES.filter((a) => {
    const st = cellStatus(matrix.cells.get(cellKey(a.id, ROUND1_SENDER)));
    return st !== 'untested' && st !== 'young';
  }).length;

  return (
    <MetaShell
      title="Produksjon"
      lead={
        <>
          Hver annonse er én kombinasjon: en <b className="text-fg">vinkel</b> (hvorfor hun klikker) og en{' '}
          <b className="text-fg">avsender</b> (hvem som sier det). Hver rute under er en kombinasjon: er den laget,
          testet, og vant den? {tested} av {ANGLES.length * SENDERS.length} ruter har vært prøvd.
        </>
      }
    >
      {/* goal */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Goal label="Mål" value={`${GOAL_PER_DAY} prøvetimer/døgn`} sub="14 i uka · ~40 nye medlemmer/mnd" />
        <Goal
          label={`Nå (Meta, ${periodLabel(d)})`}
          value={`${perDay.toFixed(1).replace('.', ',')} /døgn`}
          sub="Bookinger Meta krediterer annonsene. Alle bookinger ligger i Kalender/Sannheten."
          tone={perDay >= GOAL_PER_DAY ? 'good' : 'bad'}
        />
        <Goal label="Budsjett nå" value={`${kr(liveBudget)}/dag`} sub={`Plan: ${kr(BUDGET_PER_DAY)}/dag (godkjent)`} tone={liveBudget >= BUDGET_PER_DAY * 0.9 ? 'good' : 'bad'} />
        <Goal label="Motoren" value="Rett til booking" sub="Prøvetimesiden. Skjema bare som tillegg, med samtale samme dag." />
      </div>

      {/* matrix */}
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2.5 sm:px-4">
          <div>
            <h2 className="text-section text-fg">Matrisen</h2>
            <p className="text-label text-fg-muted">
              {matrix.totals.classified} av {matrix.totals.all} annonser sortert. Nye annonser med DNA-navn sorteres helt
              presist.
            </p>
          </div>
          <Toggle
            value={period}
            onChange={setPeriod}
            options={[
              { value: 'lifetime', label: 'Hele perioden' },
              { value: 'recent', label: `Valgt periode ${periodLabel(d)}` },
            ]}
          />
        </div>
        <div className="overflow-x-auto p-3 sm:p-4">
          <div
            className="grid min-w-[900px] gap-1.5"
            style={{ gridTemplateColumns: `200px repeat(${cols.length}, minmax(0, 1fr))` }}
          >
            <div />
            {cols.map((s) => (
              <div key={s ?? 'none'} className="px-1 pb-1 text-label font-medium text-fg" title={s ? SENDER_BY_ID.get(s)?.how : undefined}>
                {s ? SENDER_BY_ID.get(s)?.name : 'Uklar avsender'}
                {s === ROUND1_SENDER && <span className="ml-1.5 rounded bg-brand-tint px-1 text-micro text-brand">Runde 1</span>}
              </div>
            ))}
            {rows.map((a) => (
              <Row key={a ?? 'none'} a={a} cols={cols} matrix={matrix.cells} pick={pick} setPick={setPick} />
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-label text-fg-muted">
            {(Object.keys(STATUS) as CellStatus[]).map((k) => (
              <span key={k} className="flex items-center gap-1.5">
                <span className={cn('size-3 rounded-sm border', STATUS[k].cls)} /> {STATUS[k].label}
              </span>
            ))}
            <span className="text-fg-subtle">Vinner: booking under 400 kr eller lead under 110 kr · Taper: over 700 / 200 kr</span>
          </div>
        </div>
      </Card>

      {pick && (
        <CellPanel
          pick={pick}
          c={matrix.cells.get(cellKey(pick.angle, pick.sender))}
          fx={fx}
          period={period}
          onClose={() => setPick(null)}
          onPreview={(id, name) => setPreview({ id, name })}
        />
      )}

      {matrix.unclassified.length > 0 && (
        <Card className="p-3 sm:p-4">
          <h2 className="text-section text-fg">Uklassifisert ({matrix.unclassified.length})</h2>
          <p className="mt-0.5 text-label text-fg-muted">
            Navnet sier verken vinkel eller avsender («V1», «General V2», «Eng»). Brukt til sammen:{' '}
            {kr(matrix.unclassified.reduce((n, f) => n + f.ad[period].spend, 0) * fx)}. Dette er grunnen til DNA-navnene.
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {matrix.unclassified
              .sort((x, y) => y.ad.lifetime.spend - x.ad.lifetime.spend)
              .map((f) => (
                <button
                  key={f.ad.id}
                  type="button"
                  onClick={() => setPreview({ id: f.ad.id, name: f.ad.name })}
                  className="rounded-full bg-surface-3 px-2 py-0.5 text-micro text-fg-muted hover:text-fg"
                >
                  {f.ad.name}
                </button>
              ))}
          </div>
        </Card>
      )}

      <div className="grid gap-3 lg:grid-cols-[1.3fr_1fr]">
        <Card className="p-3 sm:p-4">
          <h2 className="mb-2 text-section text-fg">Testplanen: tre runder</h2>
          <ol className="flex flex-col gap-2">
            {ROUNDS.map((r) => (
              <li key={r.n} className="flex gap-3 rounded-md border border-border p-2.5">
                <span className="tabular flex size-7 shrink-0 items-center justify-center rounded-full bg-surface-3 text-label font-semibold text-fg">
                  {r.n}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-body font-medium text-fg">{r.title}</span>
                    <span className="text-micro text-fg-subtle">{r.weeks}</span>
                  </div>
                  <p className="text-label text-fg-muted">{r.what}</p>
                  {r.n === 1 && (
                    <div className="mt-1.5 flex items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-3">
                        <div className="h-full bg-brand" style={{ width: `${(round1Done / ANGLES.length) * 100}%` }} />
                      </div>
                      <span className="tabular text-micro text-fg-muted">
                        {round1Done}/{ANGLES.length} vinkler testet
                      </span>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
          <p className="mt-2 text-micro text-fg-subtle">
            Hvorfor i runder: med ~2 bookinger om dagen får hver av 42 ruter nesten ingen bookinger hvis alt testes samtidig.
            Underveis leser vi hook rate, CTR og pris per lead; bookingene bekrefter vinneren.
          </p>
        </Card>
        <div className="flex flex-col gap-3">
          <Card className="p-3 sm:p-4">
            <h2 className="mb-2 text-section text-fg">Ukerytmen</h2>
            <ul className="flex flex-col gap-1.5">
              {RHYTHM.map((r) => (
                <li key={r.day} className="grid grid-cols-[72px_1fr] gap-2 text-label">
                  <span className="font-medium text-fg">{r.day}</span>
                  <span className="text-fg-muted">{r.what}</span>
                </li>
              ))}
            </ul>
          </Card>
          <Card className="p-3 sm:p-4">
            <h2 className="mb-2 text-section text-fg">Reglene</h2>
            <ul className="flex flex-col gap-1.5">
              {RULES.map((r) => (
                <li key={r.rule} className="flex items-start gap-2 text-label">
                  <Badge tone={r.tone}>{r.rule}</Badge>
                  <span className="text-fg-muted">{r.when}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>

      <NameGenerator />

      {preview && (
        <div className="fixed inset-0 z-[65]">
          <AdPreview adId={preview.id} name={preview.name} onClose={() => setPreview(null)} />
        </div>
      )}
    </MetaShell>
  );
}

function Goal({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: 'good' | 'bad' }) {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-surface p-3 shadow-card sm:p-4">
      <div className="text-label text-fg-muted">{label}</div>
      <div className={cn('mt-0.5 text-section', tone === 'good' ? 'text-good-fg' : tone === 'bad' ? 'text-bad-fg' : 'text-fg')}>{value}</div>
      <div className="mt-0.5 text-micro text-fg-subtle">{sub}</div>
    </div>
  );
}

function Row({
  a,
  cols,
  matrix,
  pick,
  setPick,
}: {
  a: AngleId | null;
  cols: (SenderId | null)[];
  matrix: Map<string, CellStats>;
  pick: Pick;
  setPick: (p: Pick) => void;
}) {
  const angle = a ? ANGLE_BY_ID.get(a) : null;
  return (
    <>
      <div className="flex items-start gap-2 py-1 pr-2">
        <span className="mt-1.5 size-2.5 shrink-0 rounded-full" style={{ background: angle?.color ?? 'var(--fg-subtle)' }} />
        <div className="min-w-0">
          <div className="text-label font-medium text-fg">{angle?.name ?? 'Uklar vinkel'}</div>
          <div className="text-micro text-fg-subtle">{angle?.who ?? 'Navnet sier ikke hvorfor hun skal klikke.'}</div>
        </div>
      </div>
      {cols.map((s) => {
        const c = matrix.get(cellKey(a, s));
        const st = cellStatus(c);
        const on = pick?.angle === a && pick?.sender === s;
        const planned = s === ROUND1_SENDER && a !== null && a !== 'tilbud' && st === 'untested';
        return (
          <button
            key={s ?? 'none'}
            type="button"
            onClick={() => setPick(on ? null : { angle: a, sender: s })}
            aria-pressed={on}
            className={cn(
              'relative flex min-h-[76px] flex-col items-start justify-between rounded-md border p-2 text-left transition-fast hover:border-fg/40',
              STATUS[st].cls,
              on && 'ring-2 ring-fg/70',
            )}
          >
            {c ? (
              <>
                <span className="text-micro text-fg-subtle">
                  {c.ads.length} {c.ads.length === 1 ? 'annonse' : 'annonser'} · {kr(c.spend)}
                </span>
                <span className="flex flex-col gap-0.5 text-label">
                  {c.book.spend > 0 && (
                    <span className="tabular text-fg">
                      {int(c.book.n)} book · {c.book.n ? kr(c.book.spend / c.book.n) : '–'}
                    </span>
                  )}
                  {c.lead.spend > 0 && (
                    <span className="tabular text-fg-muted">
                      {int(c.lead.n)} lead · {c.lead.n ? kr(c.lead.spend / c.lead.n) : '–'}
                    </span>
                  )}
                </span>
              </>
            ) : (
              <span className="text-micro text-fg-subtle">{planned ? 'Lag i runde 1' : '–'}</span>
            )}
            {planned && <span className="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-brand" />}
          </button>
        );
      })}
    </>
  );
}

function CellPanel({
  pick,
  c,
  fx,
  period,
  onClose,
  onPreview,
}: {
  pick: NonNullable<Pick>;
  c: CellStats | undefined;
  fx: number;
  period: Period;
  onClose: () => void;
  onPreview: (id: string, name: string) => void;
}) {
  const angle = pick.angle ? ANGLE_BY_ID.get(pick.angle) : null;
  const sender = pick.sender ? SENDER_BY_ID.get(pick.sender) : null;
  const st = cellStatus(c);
  const ads = [...(c?.ads ?? [])].sort((x, y) => y.ad[period].spend - x.ad[period].spend);
  const next =
    pick.angle && pick.sender
      ? dnaName({ angle: pick.angle, sender: pick.sender, format: pick.sender === 'bilde' ? 'bilde' : 'video', hook: 1, dest: 'book', version: (c?.ads.length ?? 0) + 1 })
      : null;
  return (
    <Card className="overflow-hidden" style={{ borderTop: `3px solid ${angle?.color ?? 'var(--border)'}` }}>
      <div className="flex items-start justify-between gap-2 border-b border-border px-3 py-2.5 sm:px-4">
        <div>
          <div className="text-micro font-semibold uppercase tracking-wider text-fg-subtle">Ruten</div>
          <h2 className="text-section text-fg">
            {angle?.name ?? 'Uklar vinkel'} × {sender?.name ?? 'Uklar avsender'}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={st === 'winner' ? 'good' : st === 'loser' ? 'bad' : 'neutral'}>{STATUS[st].label}</Badge>
          <button type="button" onClick={onClose} aria-label="Lukk" className="flex size-8 items-center justify-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg">
            <X className="size-4" aria-hidden />
          </button>
        </div>
      </div>
      <div className="grid gap-4 p-3 sm:p-4 lg:grid-cols-[1.4fr_1fr]">
        <div className="min-w-0">
          <div className="mb-1.5 text-micro font-semibold uppercase tracking-wider text-fg-subtle">
            Annonsene i ruten ({ads.length})
          </div>
          {ads.length ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-label">
                <thead className="text-left text-fg-subtle">
                  <tr>
                    <th className="py-1.5 pr-2 font-medium">Annonse</th>
                    <th className="py-1.5 pr-2 text-right font-medium">Brukt</th>
                    <th className="py-1.5 pr-2 text-right font-medium">Resultat</th>
                    <th className="py-1.5 pr-2 text-right font-medium">Per stk</th>
                    <th className="py-1.5 text-right font-medium">Hook</th>
                  </tr>
                </thead>
                <tbody>
                  {ads.map(({ ad, adset }) => {
                    const p = ad[period];
                    const kind = resultKind(adset);
                    const n = kind === 'booking' ? p.schedules : p.leads;
                    return (
                      <tr key={ad.id} className="cursor-pointer border-t border-border hover:bg-surface-2" onClick={() => onPreview(ad.id, ad.name)} title="Se annonsen">
                        <td className="max-w-[260px] py-1.5 pr-2">
                          <div className="truncate text-fg">{ad.name}</div>
                          <div className="truncate text-micro text-fg-subtle">
                            {short(adset.name)} · {kind === 'booking' ? 'til booking' : 'skjema'} {isLive(ad) && isLive(adset) ? '· aktiv' : ''}
                          </div>
                        </td>
                        <td className="tabular py-1.5 pr-2 text-right text-fg">{kr(p.spend * fx)}</td>
                        <td className="tabular py-1.5 pr-2 text-right text-fg">
                          {int(n)} {kind === 'booking' ? 'book' : 'lead'}
                        </td>
                        <td className="tabular py-1.5 pr-2 text-right text-fg">{n ? kr((p.spend * fx) / n) : '–'}</td>
                        <td className="tabular py-1.5 text-right text-fg">{p.video3s ? pctStr(p.video3s / Math.max(1, p.impressions), 0) : '–'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="rounded-md border border-dashed border-border p-3 text-label text-fg-muted">
              Ingen annonser i denne ruten ennå. Den er ikke testet.
            </p>
          )}
        </div>
        <div className="flex flex-col gap-3">
          <div>
            <div className="mb-1 text-micro font-semibold uppercase tracking-wider text-fg-subtle">Neste annonse å lage</div>
            {sender && <p className="text-label text-fg-muted">{sender.how}</p>}
            {angle && (
              <ul className="mt-1.5 flex flex-col gap-1">
                {angle.hooks.map((h) => (
                  <li key={h} className="rounded-md border border-dashed border-border px-2.5 py-1.5 text-label text-fg">
                    {h}
                  </li>
                ))}
              </ul>
            )}
            {angle?.guard && <p className="mt-1.5 rounded-md bg-warn-tint px-2.5 py-1.5 text-label text-warn-fg">Pass på: {angle.guard}</p>}
          </div>
          {next && (
            <div>
              <div className="mb-1 text-micro font-semibold uppercase tracking-wider text-fg-subtle">Navn i Meta</div>
              <CopyLine text={next} />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function CopyLine({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <div className="flex items-center gap-2 rounded-md bg-surface-2 p-2">
      <code className="min-w-0 flex-1 break-all text-label text-fg">{text}</code>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(text).then(() => {
            setDone(true);
            window.setTimeout(() => setDone(false), 1400);
          });
        }}
        className="flex size-8 shrink-0 items-center justify-center rounded-md text-fg-subtle hover:bg-surface-3 hover:text-fg"
        aria-label="Kopier"
        title="Kopier"
      >
        {done ? <Check className="size-4 text-good-fg" /> : <Copy className="size-4" />}
      </button>
    </div>
  );
}

function NameGenerator() {
  const [angle, setAngle] = useState<AngleId>('ledd');
  const [sender, setSender] = useState<SenderId>('ai');
  const [format, setFormat] = useState<Format>('video');
  const [hook, setHook] = useState(1);
  const [dest, setDest] = useState<Dest>('book');
  const [version, setVersion] = useState(1);
  const sel = 'h-9 rounded-md border border-border bg-surface px-2 text-label text-fg';
  return (
    <Card className="p-3 sm:p-4">
      <h2 className="text-section text-fg">Navngeneratoren</h2>
      <p className="mt-0.5 text-label text-fg-muted">
        Hver ny annonse får navnet sitt herfra. Da sorterer matrisen den av seg selv, og vi kan se hva som virker per vinkel, avsender
        og destinasjon.
      </p>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <label className="flex flex-col gap-1 text-micro text-fg-subtle">
          Vinkel
          <select className={sel} value={angle} onChange={(e) => setAngle(e.target.value as AngleId)}>
            {ANGLES.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-micro text-fg-subtle">
          Avsender
          <select className={sel} value={sender} onChange={(e) => setSender(e.target.value as SenderId)}>
            {SENDERS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-micro text-fg-subtle">
          Format
          <select className={sel} value={format} onChange={(e) => setFormat(e.target.value as Format)}>
            {FORMATS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-micro text-fg-subtle">
          Hook nr.
          <input className={sel} type="number" min={1} max={99} value={hook} onChange={(e) => setHook(Math.max(1, Number(e.target.value) || 1))} />
        </label>
        <label className="flex flex-col gap-1 text-micro text-fg-subtle">
          Destinasjon
          <select className={sel} value={dest} onChange={(e) => setDest(e.target.value as Dest)}>
            <option value="book">Booking (prøvetimesiden)</option>
            <option value="lead">Leadskjema</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-micro text-fg-subtle">
          Versjon
          <input className={sel} type="number" min={1} max={99} value={version} onChange={(e) => setVersion(Math.max(1, Number(e.target.value) || 1))} />
        </label>
      </div>
      <div className="mt-3">
        <CopyLine text={dnaName({ angle, sender, format, hook, dest, version })} />
      </div>
    </Card>
  );
}
